import { useState } from 'react'
import type { AssessmentQuestion } from '../../types'
import { Alert, Button, Card, CardBody, CardHeader } from '../../components/ui'
import { CheckIcon } from '../../components/icons'

interface QuizResult {
  passed: boolean
  score: number
  pass_mark: number
}

export default function QuizForm({
  questions, passMark, submitting, result, onSubmit, onContinue,
}: {
  questions: AssessmentQuestion[]
  passMark: number
  submitting: boolean
  result: QuizResult | null
  onSubmit: (answers: number[]) => void
  onContinue: () => void
}) {
  const [answers, setAnswers] = useState<Record<number, number>>({})

  const allAnswered = questions.every((_, i) => answers[i] !== undefined)

  if (result) {
    return (
      <Card>
        <CardBody>
          <div className="stack" style={{ alignItems: 'center', textAlign: 'center', padding: 'var(--sp-4) 0' }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', display: 'flex', alignItems: 'center',
              justifyContent: 'center', background: result.passed ? 'var(--success-bg)' : 'var(--danger-bg)',
              color: result.passed ? 'var(--success)' : 'var(--danger)',
            }}>
              <CheckIcon size={26} />
            </div>
            <h2 style={{ margin: 0 }}>{result.passed ? 'Passed!' : 'Not quite'}</h2>
            <p className="page-head__sub" style={{ margin: 0 }}>
              You scored {result.score}%. {result.passed ? '' : `A score of ${result.pass_mark}% is required.`}
            </p>
            <Button onClick={onContinue}>
              {result.passed ? 'Continue' : 'Try again'}
            </Button>
          </div>
        </CardBody>
      </Card>
    )
  }

  return (
    <>
      <Card style={{ marginBottom: 'var(--sp-6)' }}>
        <CardBody>
          <p style={{ margin: 0 }}>
            Answer all {questions.length} question{questions.length === 1 ? '' : 's'} below.
            A score of {passMark}% or higher is required to pass.
          </p>
        </CardBody>
      </Card>

      <div className="stack" style={{ gap: 'var(--sp-4)' }}>
        {questions.map((q, qi) => (
          <Card key={qi}>
            <CardHeader title={`${qi + 1}. ${q.text}`} />
            <CardBody>
              <div className="stack" role="radiogroup" aria-label={q.text}>
                {q.options.filter((o) => o !== '').map((opt, oi) => (
                  <label key={oi} className={`radio-opt${answers[qi] === oi ? ' radio-opt--checked' : ''}`}>
                    <input
                      type="radio"
                      name={`q${qi}`}
                      checked={answers[qi] === oi}
                      onChange={() => setAnswers((a) => ({ ...a, [qi]: oi }))}
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </CardBody>
          </Card>
        ))}
      </div>

      {!allAnswered && (
        <div style={{ marginTop: 'var(--sp-4)' }}>
          <Alert tone="info">Answer every question to submit.</Alert>
        </div>
      )}

      <div className="row" style={{ justifyContent: 'flex-end', marginTop: 'var(--sp-6)' }}>
        <Button
          disabled={!allAnswered}
          loading={submitting}
          onClick={() => onSubmit(questions.map((_, i) => answers[i]))}
        >
          Submit
        </Button>
      </div>
    </>
  )
}
